/**
 * <jgd-rcc-product-list> — kompakte Empfehlungs-Produktliste gegen die
 * Nimstrata RCC Storefront API (siehe blocks/jgd-rcc-product-list.liquid).
 *
 * Ablauf:
 * 1. predict (geteilte Logik in jgd-rcc-api.js) → Produkt-IDs in Ranking-Reihenfolge.
 * 2. Zeilen-HTML über die interne Section jgd-rcc-list-rows per
 *    Dateinamen-Section-Rendering im Predictive-Search-Kontext holen
 *    (/search/suggest versteht id:-Queries; max. 10 IDs pro Request).
 * 3. Zeilen in Predict-Reihenfolge sortieren und das Skeleton ersetzen.
 *
 * Kauf-Button (nur Ein-Varianten-Produkte): POST /cart/add.js, danach wird
 * ein cart:update-Event mit source "jgd-rcc-product-list" dispatcht — das
 * Post-Add-Modal reagiert nur auf source "product-form-component" und
 * öffnet sich dadurch NICHT erneut; Zuhörer wie jgd-availability
 * aktualisieren sich trotzdem.
 */

import { predictProductIds } from './jgd-rcc-api.js';

const ROWS_SECTION = 'jgd-rcc-list-rows';

class JgdRccProductList extends HTMLElement {
  /** @type {AbortController} */
  #controller;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });

    // Lazy-Load (data-lazy-load): Predict-Call erst bei Sichtbarkeit — im
    // geschlossenen Post-Add-Modal feuert der Observer erst, wenn das Modal
    // tatsächlich geöffnet wird (unsichtbare Elemente intersecten nie).
    // Im Theme-Editor immer sofort laden.
    if (this.hasAttribute('data-lazy-load') && !this.hasAttribute('data-design-mode') && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#disconnectObserver();
          this.#load();
        },
        { rootMargin: '200px' }
      );
      this.#observer.observe(this);
    } else {
      this.#load();
    }
  }

  disconnectedCallback() {
    this.#disconnectObserver();
    this.#controller.abort();
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #rows() {
    return this.querySelector('[ref="rows"]');
  }

  async #load() {
    const { project, servingConfig, pageSize, productId, inStockOnly } = this.dataset;
    if (!project || !servingConfig) return this.#empty();

    try {
      const prediction = await predictProductIds({
        project,
        servingConfig,
        pageSize,
        productId,
        inStockOnly: this.hasAttribute('data-in-stock-only'),
        signal: this.#controller.signal,
      });
      const ids = (prediction?.ids ?? []).slice(0, Number(pageSize) || 10);
      if (!ids.length) return this.#empty();

      const rendered = await this.#renderRows(ids);
      if (!rendered) this.#empty();
    } catch {
      if (!this.#controller.signal.aborted) this.#empty();
    }
  }

  /** @param {string[]} ids */
  async #renderRows(ids) {
    const root = window.Shopify?.routes?.root ?? '/';
    const params = new URLSearchParams({
      q: ids.map((id) => `id:${id}`).join(' OR '),
      'resources[type]': 'product',
      'resources[limit]': String(Math.min(ids.length, 10)),
      section_id: ROWS_SECTION,
    });
    const response = await fetch(`${root}search/suggest?${params}`, { signal: this.#controller.signal });
    if (!response.ok) return false;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const rows = [...doc.querySelectorAll('[ref="rccRows"] [data-rcc-product-id]')];
    if (!rows.length) return false;

    const order = new Map(ids.map((id, index) => [String(id), index]));
    rows.sort(
      (a, b) =>
        (order.get(a.dataset.rccProductId) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(b.dataset.rccProductId) ?? Number.MAX_SAFE_INTEGER)
    );

    const container = this.#rows;
    if (!container) return false;
    container.replaceChildren(...rows);
    return true;
  }

  /** Direkt-Add für Ein-Varianten-Produkte. */
  #onClick = async (event) => {
    const button = event.target.closest('.jgd-rcc-plist__add');
    if (!button || button.hasAttribute('data-added')) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;
    const quantity = Number(button.dataset.quantitySteps) || 1;

    button.disabled = true;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
      });
      if (!response.ok) throw new Error(`cart/add ${response.status}`);

      button.setAttribute('data-added', '');
      button.textContent = `✓ ${button.dataset.addedLabel ?? ''}`.trim();

      const cart = await (await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)).json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: String(variantId),
            data: { source: 'jgd-rcc-product-list', itemCount: cart?.item_count, variantId: String(variantId) },
          },
        })
      );
    } catch {
      button.disabled = false;
    } finally {
      if (button.hasAttribute('data-added')) button.disabled = false;
    }
  };

  /** Keine Inhalte: Block ausblenden (im Editor bleibt das Skeleton stehen). */
  #empty() {
    if (this.hasAttribute('data-design-mode')) return;
    this.setAttribute('data-empty', '');
  }
}

if (!customElements.get('jgd-rcc-product-list')) {
  customElements.define('jgd-rcc-product-list', JgdRccProductList);
}
